<template>
  <div
    class="acSwitch"
    :class="{
      '-is-dirty': modelValue,
      '-is-disabled': disabled
    }"
    aria-selected="false"
    @click="onClick"
  >
    <div class="acSwitch_control">
      <div class="acSwitch_slot">
        <label
          v-if="label"
          :for="switchId"
          class="acSwitch_label"
        >
          {{ label }}
        </label>

        <div class="acSwitch_input">
          <input :aria-checked="modelValue" :id="switchId" role="switch" type="checkbox" :aria-disabled="disabled" :value="modelValue">
          <div class="acSwitch_track"/>
          <div
            class="acSwitch_thumb"
            aria-selected="true"
          >
            <slot name="icon">
              <div
                v-if="loading"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                class="acSwitch_progress"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="22.857142857142858 22.857142857142858 45.714285714285715 45.714285714285715">
                  <circle fill="transparent" cx="45.714285714285715" cy="45.714285714285715" r="20" stroke-width="5.714285714285714" stroke-dasharray="125.664" stroke-dashoffset="125.66370614359172px" class="acSwitch_progress_overlay"></circle>
                </svg>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'

import uuid from 'uuid/v4'

export default defineComponent({
  name: 'AcSwitch',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'click'],
  setup (props, ctx) {
    const switchId = ref(`switch-${uuid()}`)
    function onClick () {
      if (!props.loading) {
        const nVal = !props.modelValue
        ctx.emit('update:modelValue', nVal)
        ctx.emit('click', nVal)
      }
    }

    return {
      switchId,
      onClick
    }
  }
})
</script>

<style lang="scss">
  .acSwitch {
    align-items: flex-start;
    display: flex;
    flex: 0 1 auto;
    font-size: 16px;
    letter-spacing: normal;
    max-width: 100%;
    text-align: left;

    &_label {
      align-items: center;
      display: inline-flex;
      flex: 1 1 auto;
      height: auto;
      position: relative;
      left: 0;
      right: auto;
      cursor: pointer;
      user-select: none;
      font-size: 16px;
      line-height: 20px;
      min-height: 8px;
      transition: .3s cubic-bezier(.25,.8,.5,1);
    }

    &_contorl {
      display: flex;
      flex-direction: column;
      height: auto;
      flex-grow: 1;
      flex-wrap: wrap;
      min-width: 0;
      width: 100%;
    }

    &_slot {
      align-items: center;
      color: inherit;
      display: flex;
      min-height: inherit;
      position: relative;
      transition: .3s cubic-bezier(.25,.8,.5,1);
      width: 100%;
    }

    &.-is-dirty.-is-disabled {
      opacity: .6;
    }
    input[role="switch"] {
      position: absolute;
      opacity: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
      user-select: none
    }

    &.-is-dirty &_thumb {
      transform: translate(23px);
    }

    &_thumb {
      left: 3px;
      background-color: currentColor;
      pointer-events: none;
      color: #fff;
      border-radius: 50%;
      position: relative;
      height: 24px;
      width: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: .3s cubic-bezier(.25,.8,.5,1);
    }

    &_track {
      background-color: currentColor;
      pointer-events: none;
      transition: inherit;
      color: #C8C8C8;
      border-radius: 15px;
      width: 52px;
      height: 30px;
      left: 0;
      position: absolute;
      right: 0;
    }

    &.-is-dirty &_track {
      color: #51DA3B;
    }

    &.-is-disabled:not(&.-is-dirty) &_thumb {
      color: #fafafa !important
    }

    &.-is-disabled:not(&.-is-dirty) &_track {
      color: rgba(0,0,0,.12)!important
    }

    &_input {
      width: 52px;
      height: 30px;
      display: flex;
      align-items: center;
    }

    &_progress {
      height: 24px;
      width: 25px;
      color: #fb8c00 !important;
      caret-color: #fb8c00 !important;
      position: relative;
      display: inline-flex;
      vertical-align: middle;
      justify-content: center;
      align-items: center;

      svg {
        animation: progress-circular-rotate 1.4s linear infinite;
        transform-origin: center center;
        transition: all .2s ease-in-out;

        width: 100%;
        height: 100%;
        margin: auto;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 0;

        &:not([fill]) {
          fill: currentColor;
        }
      }

      &_overlay {
        animation: progress-circular-dash 1.4s ease-in-out infinite;
        stroke-linecap: round;
        stroke-dasharray: 80, 200;
        stroke-dashoffset: 0;
        stroke: currentColor;
        z-index: 2;
        transition: all .6s ease-in-out;
      }
    }
  }

  @-webkit-keyframes progress-circular-rotate {
    to {
      transform: rotate(1turn)
    }
  }

  @keyframes progress-circular-rotate {
    to {
      transform: rotate(1turn)
    }
  }
</style>
